import { Box, Card, CircularProgress, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { BudgetDashboardGroupItem } from "../../../types/dashboard";
import { formatPercent } from "../utils";

interface TopClientsChartProps {
  data: BudgetDashboardGroupItem[];
  isLoading: boolean;
}

const truncate = (value: string, max = 18) =>
  value.length > max ? `${value.slice(0, max - 1)}…` : value;

// O LabelList padrão herda a largura da barra e quebra o texto em várias
// linhas; este content desenha o rótulo numa linha só, à direita da barra.
const BarLabel = ({ x, y, width, height, value }: any) => (
  <text
    x={Number(x) + Number(width) + 8}
    y={Number(y) + Number(height) / 2}
    dominantBaseline="middle"
    textAnchor="start"
    style={{ fontSize: 11, fontWeight: 600, fill: "#4A2F8F" }}
  >
    {value}
  </text>
);

export const TopClientsChart = ({ data, isLoading }: TopClientsChartProps) => {
  // O rótulo é pré-calculado porque o formatter do LabelList recebe apenas o
  // valor da barra, sem acesso ao restante do registro.
  const chartData = data.map((item) => ({
    ...item,
    shortName: truncate(item.name),
    barLabel: `${formatPercent(item.adoptionRate)} (${
      item.totalProjects
    } proj)`,
  }));

  return (
    <Card
      elevation={0}
      sx={{
        p: "18px 20px",
        height: "100%",
        boxSizing: "border-box",
        borderRadius: "12px",
        border: "1px solid #E4E7EC",
      }}
    >
      <Typography fontSize={15} fontWeight={700}>
        Top Clientes
      </Typography>
      <Typography color="#6B7686" fontSize={12.5} mb="14px">
        Clientes com mais projetos no período e sua taxa de adoção.
      </Typography>

      <Box height={340}>
        {isLoading ? (
          <Box
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <CircularProgress />
          </Box>
        ) : !chartData.length ? (
          <Box
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Typography color="#94A3B8">Sem dados no período.</Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ left: 8, right: 96 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis
                type="number"
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
              />
              <YAxis
                type="category"
                dataKey="shortName"
                width={148}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value: any, _name: string, entry: any) => [
                  `${formatPercent(Number(value))} de adoção · ${
                    entry?.payload?.totalProjects ?? 0
                  } projeto(s)`,
                  entry?.payload?.name ?? "Cliente",
                ]}
              />
              <Bar
                dataKey="adoptionRate"
                fill="#7C3AED"
                radius={[0, 4, 4, 0]}
                barSize={18}
              >
                <LabelList dataKey="barLabel" content={BarLabel} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Card>
  );
};
